import React, { useState, useEffect } from 'react';
import API from '../../utils/api';
import { FiCalendar, FiClock, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { format } from 'date-fns';
import { convertToINR } from '../../utils/currency';

const BuyerBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const { data } = await API.get('/bookings');
      setBookings(data.bookings);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredBookings = filter === 'all' 
    ? bookings 
    : bookings.filter(b => b.status.toLowerCase() === filter);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-[#DDF5E8] text-[#386646]';
      case 'Confirmed': return 'bg-[#FCD06B] text-[#386646]';
      case 'Pending': return 'bg-[#FFE4B5] text-[#B45309]';
      case 'In Progress': return 'bg-[#FFF5B8] text-[#386646]';
      case 'Cancelled': return 'badge-danger';
      case 'Rejected': return 'badge-danger';
      default: return 'bg-[#FFF5B8] text-[#386646]';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF9D4] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[rgba(34,34,34,0.7)]">Loading bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF9D4] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-display font-bold text-[#222222] mb-2">
            My Bookings
          </h1>
          <p className="text-[rgba(34,34,34,0.7)]">Track and manage your rental orders</p>
        </div>

        {/* Filter Tabs */}
        <div className="mb-6 flex flex-wrap gap-2">
          {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                filter === status
                  ? 'bg-[#386646] text-white'
                  : 'bg-[#FFF5B8] text-[#386646] hover:bg-[#FCD06B]'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {/* Bookings List */}
        {filteredBookings.length === 0 ? (
          <div className="card p-12 text-center">
            <div className="text-6xl mb-4">📦</div>
            <h3 className="text-2xl font-semibold text-[#222222] mb-2">
              No bookings found
            </h3>
            <p className="text-[rgba(34,34,34,0.7)] mb-6">
              Start exploring and rent your first item!
            </p>
            <a href="/products" className="btn-primary inline-block">
              Browse Products
            </a>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredBookings.map((booking) => (
              <div key={booking._id} className="card overflow-hidden hover:shadow-lg transition-shadow">
                {/* Status Banner */}
                {booking.status === 'Confirmed' && (
                  <div className="bg-green-50 border-l-4 border-green-500 p-4 flex items-center space-x-3">
                    <FiCheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-green-700">✓ Your order has been approved!</p>
                      <p className="text-sm text-green-600">The seller has confirmed your rental request.</p>
                    </div>
                  </div>
                )}
                {booking.status === 'Rejected' && (
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 flex items-center space-x-3">
                    <FiXCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-red-700">✗ Your order has been rejected</p>
                      <p className="text-sm text-red-600">The seller declined your rental request. Please try another product.</p>
                    </div>
                  </div>
                )}
                {booking.status === 'In Progress' && (
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 flex items-center space-x-3">
                    <FiCheckCircle className="w-6 h-6 text-blue-500 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-blue-700">📦 Your rental is in progress</p>
                      <p className="text-sm text-blue-600">Please take care of the product and return it on time.</p>
                    </div>
                  </div>
                )}
                {booking.status === 'Completed' && (
                  <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 flex items-center space-x-3">
                    <FiCheckCircle className="w-6 h-6 text-emerald-500 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-emerald-700">✓ Rental completed successfully!</p>
                      <p className="text-sm text-emerald-600">Thank you for your business. Please leave a review.</p>
                    </div>
                  </div>
                )}

                {/* Booking Content */}
                <div className="p-6">
                  <div className="flex flex-col md:flex-row gap-6">
                    {/* Product Image */}
                    <div className="flex-shrink-0">
                      <img
                        src={booking.product?.images?.[0] || 'https://via.placeholder.com/150'}
                        alt={booking.product?.title}
                        className="w-full md:w-32 h-32 object-cover rounded-lg"
                      />
                    </div>

                    {/* Booking Details */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-xl font-semibold text-[#222222] mb-1">
                            {booking.product?.title}
                          </h3>
                          <p className="text-[rgba(34,34,34,0.7)]">
                            Rented from: {booking.seller?.name}
                          </p>
                        </div>
                        <span className={`badge ${getStatusColor(booking.status)}`}>
                          {booking.status}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="flex items-center space-x-2 text-[rgba(34,34,34,0.7)]">
                          <FiCalendar className="text-[#F27D16]" />
                          <div>
                            <p className="text-xs text-[rgba(34,34,34,0.7)]">Start Date</p>
                            <p className="font-medium">
                              {format(new Date(booking.startDate), 'MMM dd, yyyy')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 text-[rgba(34,34,34,0.7)]">
                          <FiCalendar className="text-[#F27D16]" />
                          <div>
                            <p className="text-xs text-[rgba(34,34,34,0.7)]">End Date</p>
                            <p className="font-medium">
                              {format(new Date(booking.endDate), 'MMM dd, yyyy')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 text-[rgba(34,34,34,0.7)]">
                          <FiClock className="text-[#F27D16]" />
                          <div>
                            <p className="text-xs text-[rgba(34,34,34,0.7)]">Duration</p>
                            <p className="font-medium">{booking.totalDays} days</p>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-[#FCD06B]">
                        <div>
                          <p className="text-sm text-[rgba(34,34,34,0.7)]">Total Amount</p>
                          <p className="text-2xl font-bold text-[#F27D16]">
                            ₹{convertToINR(booking.totalAmount).toLocaleString('en-IN')}
                          </p>
                        </div>
                        {booking.status === 'Pending' && (
                          <button className="btn-secondary">
                            Cancel Booking
                          </button>
                        )}
                        {booking.status === 'Completed' && (
                          <button className="btn-primary">
                            Write Review
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BuyerBookings;
